import React, { useState, useEffect } from 'react';
import { getTodayISO } from '../lib/dateUtils';
import { FuelLogEntry } from '../lib/fuelLogApi';

interface FuelLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { liter: number; price: number; date: string }) => void;
  initialData?: Partial<FuelLogEntry>;
}

const FuelLogForm: React.FC<FuelLogFormProps> = ({ open, onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState({
    liter: initialData?.liter?.toString() || '',
    price: initialData?.price?.toString() || '',
    date: initialData?.date || getTodayISO(),
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm({
        liter: initialData?.liter?.toString() || '',
        price: initialData?.price?.toString() || '',
        date: initialData?.date || getTodayISO(),
      });
    }
  }, [open, initialData]);

  if (!open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.liter || !form.price || !form.date) {
      setError('All fields are required.');
      return;
    }
    // Allow comma as decimal separator
    const literNum = Number(form.liter.replace(',', '.'));
    const priceNum = Number(form.price.replace(',', '.'));
    if (isNaN(literNum) || isNaN(priceNum) || literNum <= 0 || priceNum <= 0) {
      setError('Liters and price must be numbers greater than zero. Use a comma for decimals, e.g. 12,34.');
      return;
    }
    setError('');
    onSubmit({
      liter: literNum,
      price: priceNum,
      date: form.date,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-theme-primary rounded-lg p-6 shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-theme-primary">{initialData ? 'Edit' : 'Add'} Fuel Entry</h2>
          <button onClick={onClose} className="text-theme-secondary hover:text-theme-primary">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-theme-secondary mb-1">Liters*</label>
            <input
              type="text"
              name="liter"
              value={form.liter}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-theme-primary bg-theme-secondary text-theme-primary focus:outline-none"
              required
              inputMode="decimal"
              pattern="[0-9]+([,\.][0-9]{1,2})?"
              placeholder="npr. 12,34"
            />
          </div>
          <div>
            <label className="block text-theme-secondary mb-1">Total Price*</label>
            <input
              type="text"
              name="price"
              value={form.price}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-theme-primary bg-theme-secondary text-theme-primary focus:outline-none"
              required
              inputMode="decimal"
              pattern="[0-9]+([,\.][0-9]{1,2})?"
              placeholder="npr. 2500,50"
            />
          </div>
          <div>
            <label className="block text-theme-secondary mb-1">Date*</label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-theme-primary bg-theme-secondary text-theme-primary focus:outline-none"
              required
            />
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-theme-secondary text-theme-primary border border-theme-primary hover:bg-theme-tertiary">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-md bg-primary-600 text-white font-medium hover:bg-primary-700">{initialData ? 'Save' : 'Add'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FuelLogForm; 